import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';

interface MetalDistributionData {
    readonly metal_category: string;
    readonly contract_count: number;
    readonly total_grams: number;
    readonly total_value_ron: number;
    readonly avg_price_per_gram: number;
}

interface MetalDistributionChartProps {
    readonly data: readonly MetalDistributionData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function MetalDistributionChart({
    data,
    loading = false,
    currency = 'EUR',
}: MetalDistributionChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currSuffix = currency === 'EUR' ? 'EUR' : 'RON';

    const pieData = data.map(d => ({
        name: d.metal_category,
        y: Number(d.total_value_ron) || 0,
        contracts: d.contract_count,
        grams: d.total_grams,
    }));

    const options: Highcharts.Options = {
        chart: {
            type: 'pie',
            height: 350,
        },
        title: {
            text: undefined,
        },
        tooltip: {
            pointFormat:
                '<b>{point.percentage:.1f}%</b><br/>' +
                t('statistics.bi.value', 'Value') +
                `: {point.y:,.0f} ${currSuffix}<br/>` +
                t('statistics.bi.contracts', 'Contracts') +
                ': {point.contracts}<br/>' +
                t('statistics.bi.grams', 'Grams') +
                ': {point.grams:,.2f}',
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                },
                showInLegend: true,
            },
        },
        series: [
            {
                type: 'pie',
                name: t('statistics.bi.metalDistribution', 'Metal Distribution'),
                data: pieData,
                colors: ['#faad14', '#ffc53d', '#d4b106', '#bfbfbf', '#8c8c8c'],
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default MetalDistributionChart;
